import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Target, TrendingUp, Brain, ArrowRight, Trophy, Users } from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { useCourseCategories } from '@/hooks/useCourseCategories';
import { useUnifiedCourseCounts } from '@/hooks/useUnifiedCourseCounts';
import AnimatedCounter from '@/components/common/AnimatedCounter';
import { NeuralNetworkBackground } from './NeuralNetworkBackground';
import { LiveCodePreview } from './LiveCodePreview';
import '@/styles/futuristic.css';

const HeroSection = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { session } = useSupabaseAuth();
  const { categories } = useCourseCategories();
  const { totalCourses, totalCategories, loading: countsLoading } = useUnifiedCourseCounts();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const elements = document.querySelectorAll('.magnetic-hover');
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        (el as HTMLElement).style.transform = `translate(${x * 0.1}px, ${y * 0.1}px)`;
      });
    };

    const handleMouseLeave = () => {
      const elements = document.querySelectorAll('.magnetic-hover');
      elements.forEach((el) => {
        (el as HTMLElement).style.transform = 'translate(0, 0)';
      });
    };

    const hero = heroRef.current;
    if (hero) {
      hero.addEventListener('mousemove', handleMouseMove);
      hero.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (hero) {
        hero.removeEventListener('mousemove', handleMouseMove);
        hero.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden breathing-bg"
    >
      {/* Neural Network Background */}
      <NeuralNetworkBackground />
      
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-accent/10 pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10 text-center pt-24">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Animated Stats Bar - Touch-Friendly & Scrollable */}
          <div className="flex items-center justify-center gap-3 overflow-x-auto pb-2 mb-6 animate-fade-in no-scrollbar snap-x snap-mandatory">
            <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20 hover:bg-primary/15 transition-colors min-h-[48px] snap-start shrink-0">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              <span className="text-sm font-medium">AI Tutor</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20 hover:bg-primary/15 transition-colors min-h-[48px] snap-start shrink-0">
              <Trophy className="h-4 w-4 text-primary animate-float" />
              <span className="text-sm font-medium">
                {countsLoading ? (
                  <span className="inline-block w-16 h-4 bg-primary/20 rounded animate-pulse" />
                ) : (
                  <><AnimatedCounter end={totalCourses} suffix="+" /> Courses</>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20 hover:bg-primary/15 transition-colors min-h-[48px] snap-start shrink-0">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                {countsLoading ? (
                  <span className="inline-block w-16 h-4 bg-primary/20 rounded animate-pulse" />
                ) : (
                  <><AnimatedCounter end={totalCategories} suffix="+" /> Categories</>
                )}
              </span>
            </div>
          </div>

          {/* Main Heading with Optimized Mobile Typography */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-satoshi leading-tight mb-6 animate-fade-in px-4">
            <span className="bg-gradient-to-r from-foreground via-primary to-accent bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto]">
              Master Technology
            </span>
            <br />
            <span className="text-foreground">with AI-Powered Learning</span>
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-loose px-4 animate-fade-in" style={{ animationDelay: '100ms' }}>
            Your personal AI tutor guides you through curated courses in React, Python, SQL, Cloud Technologies, and more. Learn at your pace, master real-world skills.
          </p>

          {/* Live Code Preview */}
          <div className="mb-10 animate-fade-in" style={{ animationDelay: '150ms' }}>
            <LiveCodePreview />
          </div>

          {/* CTA Buttons - Touch-Optimized */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in px-4 mb-12" style={{ animationDelay: '200ms' }}>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 magnetic-hover btn-ripple px-8 py-4 text-base font-semibold min-h-[48px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={() => {
                if (session) {
                  window.location.href = '/dashboard';
                } else {
                  window.location.href = '/login';
                }
              }}
            >
              Access Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-primary/30 hover:bg-primary/10 hover:border-primary magnetic-hover px-8 py-4 text-base font-semibold min-h-[48px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={() => scrollToSection('ai-tutor-section')}
            >
              <Brain className="mr-2 h-5 w-5 text-primary" />
              Explore AI Tutor
            </Button>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div 
            className="holographic-card glass-futuristic p-8 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer group stagger-reveal"
            onClick={() => scrollToSection('ai-features')}
          >
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 p-3 shadow-md group-hover:shadow-lg transition-shadow duration-200">
              <Brain className="w-full h-full text-white" />
            </div>
            <h2 className="font-semibold text-base mb-2">AI Tutor</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">Real-time code execution & instant help</p>
          </div>

          <div 
            className="holographic-card glass-futuristic p-8 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer group stagger-reveal"
            onClick={() => scrollToSection('interview-prep')}
          >
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 p-3 shadow-md group-hover:shadow-lg transition-shadow duration-200">
              <Target className="w-full h-full text-white" />
            </div>
            <h2 className="font-semibold text-base mb-2">Interview Mastery</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">10K+ questions with AI evaluation</p>
          </div>

          <div 
            className="holographic-card glass-futuristic p-8 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer group stagger-reveal"
            onClick={() => scrollToSection('features')}
          >
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 p-3 shadow-md group-hover:shadow-lg transition-shadow duration-200">
              <TrendingUp className="w-full h-full text-white" />
            </div>
            <h2 className="font-semibold text-base mb-2">Career Path</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">Manager-assigned learning paths</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
